import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-lg font-bold text-luxury-text mb-3 pb-2 border-b border-luxury-border">{title}</h2>
    <div className="text-luxury-secondary text-sm leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-luxury-bg">
      <PublicHeader />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-luxury-surface border-b border-luxury-border text-center px-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-luxury-gold/5 rounded-full blur-3xl" />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-luxury-elevated border border-luxury-gold/30 rounded-full text-luxury-gold text-xs font-semibold mb-6">
            <span className="w-1.5 h-1.5 bg-luxury-gold rounded-full" />
            South African Law Governed
          </div>
          <h1 className="text-4xl font-extrabold text-luxury-text mb-2">Terms & Conditions</h1>
          <p className="text-luxury-muted text-sm">Last updated: June 2025</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <div className="p-5 bg-luxury-elevated border border-luxury-gold/20 rounded-2xl mb-10">
          <div className="flex items-start gap-3">
            <span className="text-xl">⚖️</span>
            <p className="text-luxury-secondary text-sm leading-relaxed">
              Please read these Terms and Conditions carefully before using TaxVault Global. By accessing or using our platform, you agree to be bound by these terms.
            </p>
          </div>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>By creating an account or using TaxVault Global ("the Platform"), you agree to these Terms and our Privacy Policy. If you do not agree, please do not use the Platform. These terms are governed by the laws of the Republic of South Africa.</p>
        </Section>

        <Section title="2. Description of Service">
          <p>TaxVault Global provides a digital platform for South African taxpayers, businesses and accounting professionals to store and manage tax documents, track SARS submissions, calculate income tax, plan budgets and receive tax deadline reminders.</p>
          <p className="p-3 bg-luxury-elevated border border-luxury-border rounded-xl mt-3">
            <strong className="text-luxury-text">Important:</strong> TaxVault is a document management and financial organisation tool. We are not a registered tax practitioner and do not provide formal tax advice. Always consult a qualified tax professional for specific guidance.
          </p>
        </Section>

        <Section title="3. User Accounts">
          <ul className="list-none space-y-2">
            {[
              "Be 18 years of age or older",
              "Provide accurate, complete and current registration information",
              "Maintain the security of your account password",
              "Promptly notify us of any unauthorised use of your account",
              "Be responsible for all activity that occurs under your account",
            ].map(item => (
              <li key={item} className="flex gap-2">
                <span className="text-luxury-cta font-bold flex-shrink-0 mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="4. Subscription Plans & Payments">
          <ul className="list-none space-y-2">
            {[
              "Payments are processed securely via PayStack in South African Rand (ZAR)",
              "Subscriptions are billed monthly on the date of your initial subscription",
              "Prices are as published on the Platform and may be updated with 30 days' notice",
              "All fees are quoted inclusive of VAT where applicable",
            ].map(item => (
              <li key={item} className="flex gap-2">
                <span className="text-luxury-gold font-bold flex-shrink-0 mt-0.5">✦</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="5. Cancellation & Refund Policy">
          <p><strong className="text-luxury-text">Cancellations:</strong> Cancel anytime via account settings. Your plan remains active until the end of the billing period — no further charges after cancellation.</p>
          <p><strong className="text-luxury-text">Refunds:</strong></p>
          <ul className="list-none space-y-2 mt-2">
            <li className="flex gap-2">
              <span className="text-luxury-cta font-bold flex-shrink-0 mt-0.5">✓</span>
              <span><strong className="text-luxury-text">7-day money-back guarantee:</strong> Full refund within 7 days of your first payment. Email billing@taxvault.live.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-luxury-cta font-bold flex-shrink-0 mt-0.5">✓</span>
              <span><strong className="text-luxury-text">After 7 days:</strong> Non-refundable except where required by the South African Consumer Protection Act (CPA).</span>
            </li>
            <li className="flex gap-2">
              <span className="text-luxury-cta font-bold flex-shrink-0 mt-0.5">✓</span>
              <span><strong className="text-luxury-text">Disputes:</strong> Submit to disputes@taxvault.live within 30 days. We respond within 5 business days.</span>
            </li>
          </ul>
        </Section>

        <Section title="6. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-none space-y-2 mt-2">
            {[
              "Upload false, fraudulent or misleading financial or tax information",
              "Use the Platform for any unlawful purpose or in violation of SARS regulations",
              "Attempt to gain unauthorised access to other users' accounts or data",
              "Upload viruses, malware or any harmful code",
              "Scrape, copy or reproduce Platform content without written permission",
            ].map(item => (
              <li key={item} className="flex gap-2">
                <span className="text-red-500 font-bold flex-shrink-0 mt-0.5">✕</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="7. Intellectual Property">
          <p>All content, features and functionality of TaxVault Global are owned by TaxVault Global and protected by South African and international intellectual property laws. Your documents and data remain your property — we hold a limited licence to store and process them solely to provide our service.</p>
        </Section>

        <Section title="8. Disclaimer of Warranties">
          <p>TaxVault Global is provided "as is" without warranties of any kind. Tax calculations are based on publicly available SARS information and are provided for informational purposes only — they do not constitute professional tax advice.</p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p>To the maximum extent permitted by South African law, our total liability to you shall not exceed the amount paid by you for our services in the 3 months preceding the claim.</p>
        </Section>

        <Section title="10. Changes to Terms">
          <p>We may update these Terms from time to time. We will notify you of material changes via email or a notice on the Platform at least 14 days before they take effect.</p>
        </Section>

        <Section title="11. Governing Law">
          <p>These Terms are governed by the laws of the Republic of South Africa. Any disputes shall be subject to South African court jurisdiction. We encourage you to first contact us at disputes@taxvault.live to resolve any dispute informally.</p>
        </Section>

        <Section title="12. Contact Us">
          <p><strong className="text-luxury-text">Email:</strong> legal@taxvault.live</p>
          <p><strong className="text-luxury-text">Website:</strong> www.taxvault.co.za</p>
        </Section>

        <div className="flex gap-4 mt-10">
          <Link to="/privacy-policy" className="px-5 py-2.5 border border-luxury-border text-luxury-secondary rounded-xl text-sm font-semibold hover:border-luxury-gold/40 hover:text-luxury-gold transition-all">
            Privacy Policy
          </Link>
          <Link to="/contact" className="px-5 py-2.5 bg-luxury-gold hover:bg-luxury-gold-dim text-white rounded-xl text-sm font-bold transition-all">
            Contact Us
          </Link>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
